// src/components/FormRenderer.jsx
import DOMPurify from "dompurify"; // Import DOMPurify for sanitizing HTML
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SiteInformationViewer from "./SiteInformationViewer";
import imageCompression from "browser-image-compression";
import RectangleLinkButton from "../components/RectangleLinkButton";
import "dayjs/locale/en-gb";
import CustomDocumentUploader from "./CustomDocumentUploader";

import axios from "axios";
import {
  Container,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Snackbar,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormGroup,
  FormHelperText,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  InputLabel,
  Divider,
  Select,
  MenuItem,
  ListItemText,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import WorkOrdersSelector from "./WorkOrders";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import SignatureComponent from "./SignatureComponent"; // Separate Signature rendering component
import UserDetails from "./UserDetails"; // Import the UserDetails component
import HazardPopup from "./HazardPopup";
import DynamicQuestionGroupComponent from "./DynamicQuestionGroupComponent";
import UserMultiSelectRenderer from "./UserMultiSelectRenderer";
import ZoneHazardPopup from "./ZoneHazardPopup";
import RoofPlanViewer from "./RoofPlanViewer";
import ServiceProviderSelectRenderer from "./ServiceProviderSelectRenderer";
import InspectionItemField from "../modules/InspectionItemField";
import { buildAuditBanner, evaluateCombinedAuditStatus } from "../helpers/auditScoring";

// --- Audit Criticality helpers ---
const CRIT_COLORS = {
  High: "#db441f", // Red
  Medium: "#f59c16", // Orange
  Low: "#80cc5a", // Green
};

const CRIT_LEVELS = ["High", "Medium", "Low"];

const normalizeAuditCrit = (ac) => ({
  High: Array.isArray(ac?.High) ? ac.High : [],
  Medium: Array.isArray(ac?.Medium) ? ac.Medium : [],
  Low: Array.isArray(ac?.Low) ? ac.Low : [],
});

// Return the highest level that has at least one mapped option, or null if none
const getActiveCritLevel = (module) => {
  const { High, Medium, Low } = normalizeAuditCrit(module?.auditCriticality);
  if (High.length) return "High";
  if (Medium.length) return "Medium";
  if (Low.length) return "Low";
  return null;
};

// Label renderer with pill
const LabelWithPill = ({ label, level }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {level && (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: 9999,
          bgcolor: CRIT_COLORS[level],
        }}
      />
    )}
    <Typography component="span">{label}</Typography>
  </Box>
);

/** Count how many questions (not options) hit each criticality level */
const countCritQuestionHits = (modules, responses) => {
  const counts = { High: 0, Medium: 0, Low: 0 };

  const tallyModule = (mod) => {
    // Only our audit question type contributes
    if (mod.type === "audit_checkbox_group") {
      const ac = normalizeAuditCrit(mod.auditCriticality);
      const raw = responses[mod.id];
      const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];

      // A question contributes at most +1 per level if any mapped option is selected
      CRIT_LEVELS.forEach((lvl) => {
        if (ac[lvl].length && ac[lvl].some((opt) => selected.includes(opt))) {
          counts[lvl] += 1;
        }
      });
    }
    if (mod.type === "inspection_item") {
  const ac = normalizeAuditCrit(mod.auditCriticality);
  const raw = responses[mod.id];
  const sel = raw?.selection ?? (typeof raw === "string" ? raw : null);
  const selected = sel ? [sel] : [];
  CRIT_LEVELS.forEach((lvl) => {
    if (ac[lvl].length && ac[lvl].some((opt) => selected.includes(opt))) {
      counts[lvl] += 1;
    }
  });
}

    // If you ever nest audit questions under banners/sections/etc
    if (Array.isArray(mod.children)) mod.children.forEach(tallyModule);

    // Support dynamic_question_group that may contain audit questions
    if (mod.type === "dynamic_question_group" && Array.isArray(mod.questions)) {
      mod.questions.forEach((q) => {
        if (q.type === "audit_checkbox_group") {
          const ac = normalizeAuditCrit(q.auditCriticality);
          const raw = responses[q.id];
          const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];
          CRIT_LEVELS.forEach((lvl) => {
            if (
              ac[lvl].length &&
              ac[lvl].some((opt) => selected.includes(opt))
            ) {
              counts[lvl] += 1;
            }
          });
        }
      });
    }
  };

  modules.forEach(tallyModule);
  return counts;
};

// Some modules use fixed keys in `responses` instead of their numeric/string module.id
const fixedKeyForType = (type) => {
  switch ((type || "").toLowerCase()) {
    case "user_details":
      return "user_details";
    case "email":
      return "email";
    case "signature":
      return "signature";
    case "user_multi_select":
      return "linked_users";
    default:
      return null;
  }
};

// --- Per-option criticality helpers ---
const getOptionCritLevel = (module, option) => {
  const { High, Medium, Low } = normalizeAuditCrit(module?.auditCriticality);
  if (High.includes(option)) return "High";
  if (Medium.includes(option)) return "Medium";
  if (Low.includes(option)) return "Low";
  return null;
};

const OptionLabelWithPill = ({ label, level }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography component="span">{label}</Typography>
    {level && (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: 9999,
          bgcolor: CRIT_COLORS[level],
          flex: "0 0 auto",
        }}
      />
    )}
  </Box>
);

/** Given counts and a threshold bucket, decide if any level meets/exceeds it */
const meetsBucket = (counts, bucket) => {
  if (!bucket) return false;
  const needH = Number(bucket.High) || 0;
  const needM = Number(bucket.Medium) || 0;
  const needL = Number(bucket.Low) || 0;

  return (
    (needH > 0 && counts.High >= needH) ||
    (needM > 0 && counts.Medium >= needM) ||
    (needL > 0 && counts.Low >= needL)
  );
};

/** Main evaluator: returns 15 (Fail), 14 (Needs Improvement) or null */
const evaluateAuditThresholdStatus = (formStructure, responses) => {
  if (!formStructure || formStructure.formType !== "Audit") return null;
  const th = formStructure.threshold;
  if (!th || !th.enabled) return null;

  const counts = countCritQuestionHits(formStructure.modules || [], responses);

  // Fail (15) takes precedence over Needs Improvement (14)
  if (meetsBucket(counts, th.fail)) return 15;
  if (meetsBucket(counts, th.needsImprovement)) return 14;
  return null;
};

// --- Video helpers ---
const getYouTubeId = (urlStr) => {
  try {
    const u = new URL(urlStr);
    if (u.hostname.includes("youtu.be")) {
      // https://youtu.be/<id>
      return u.pathname.split("/")[1] || null;
    }
    if (u.hostname.includes("youtube.com")) {
      // https://www.youtube.com/watch?v=<id> or /embed/<id>
      if (u.pathname.startsWith("/embed/")) {
        return u.pathname.split("/")[2] || null;
      }
      return u.searchParams.get("v");
    }
  } catch {}
  return null;
};

const getVimeoId = (urlStr) => {
  try {
    const u = new URL(urlStr);
    if (u.hostname.includes("vimeo.com")) {
      // https://vimeo.com/<id> or /channels/.../<id>
      const parts = u.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      return /^\d+$/.test(last) ? last : null;
    }
  } catch {}
  return null;
};

const isDirectVideo = (urlStr) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(urlStr);

const buildEmbed = (
  urlStr,
  { autoplay = false, muted = false, loop = false } = {},
) => {
  const yt = getYouTubeId(urlStr);
  if (yt) {
    const params = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
      autoplay: autoplay ? "1" : "0",
      mute: muted ? "1" : "0",
      loop: loop ? "1" : "0",
      // for loop on YT you also need playlist=<id>
      ...(loop ? { playlist: yt } : {}),
    });
    return {
      kind: "iframe",
      src: `https://www.youtube.com/embed/${yt}?${params.toString()}`,
    };
  }

  const vimeo = getVimeoId(urlStr);
  if (vimeo) {
    const params = new URLSearchParams({
      autopause: "1",
      playsinline: "1",
      autoplay: autoplay ? "1" : "0",
      muted: muted ? "1" : "0",
      loop: loop ? "1" : "0",
      title: "0",
      byline: "0",
      portrait: "0",
    });
    return {
      kind: "iframe",
      src: `https://player.vimeo.com/video/${vimeo}?${params.toString()}`,
    };
  }

  if (isDirectVideo(urlStr)) {
    return { kind: "video", src: urlStr };
  }

  // Fallback – safest is to not iframe unknown domains.
  return { kind: "link", href: urlStr };
};

// Responsive 16/9 wrapper utility
const ResponsiveFrame = ({ aspect = 16 / 9, children }) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      pt: `${(1 / aspect) * 100}%`,
      borderRadius: 1,
      overflow: "hidden",
      bgcolor: "black",
    }}
  >
    <Box sx={{ position: "absolute", inset: 0 }}>{children}</Box>
  </Box>
);

// ---------- REQUIRED VALIDATION HELPERS ----------
const toArray = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);
const isBlankStr = (v) => (typeof v === "string" ? v.trim() === "" : v == null);

// read from flat or nested (responses[parentId][childId])
const readAnswer = (responses, id, parentId) => {
  const flat = responses?.[id];
  if (flat !== undefined) return flat;
  if (parentId && responses?.[parentId]) return responses[parentId][id];
  return undefined;
};

const isAnsweredByType = (type, value, signatures) => {
  switch (type) {
    case "short_answer":
    case "long_answer":
    case "email":
    case "datetime":
      return !isBlankStr(value);

    case "checkbox": // if you need "must be checked", use `value === true`
      return value === true || value === false;

    case "checkbox_group":
case "custom_checkbox_group":
case "audit_checkbox_group":
  return Array.isArray(value) ? value.length > 0 : !isBlankStr(value);

case "inspection_item":
  return !!(value?.selection);
  
    case "email_dropdown":
    case "dropdown_select":
      return Array.isArray(value) ? value.length > 0 : !isBlankStr(value);

    case "radio_button_group":
      return !isBlankStr(value);

    case "time_range":
      return value && value.startTime && value.endTime;

    case "date_range":
      return value && value.startDate && value.endDate;

    case "table":
      return (
        Array.isArray(value) &&
        value.some(
          (row) => row && Object.values(row).some((cell) => !isBlankStr(cell)),
        )
      );

    case "image_upload":
      return Array.isArray(value) ? value.length > 0 : false;

    case "signature":
      // check either signatures state or a string value
      return Boolean(
        (Array.isArray(signatures) && signatures.length > 0) ||
        (typeof value === "string" && value.length > 0),
      );

    case "user_details":
      return (
        value &&
        (value.email ||
          value.firstName ||
          value.lastName ||
          value.company ||
          value.phoneNumber)
      );

    case "work_orders":
      return Array.isArray(value) && value.length > 0;

    case "custom_document_uploader":
      return !!value;

    default:
      return true; // non-input/visual modules
  }
};

// 🔧 change signature so we can use responses
const resolveRenderedQuestions = (mod, responses) => {
  if (!Array.isArray(mod?.questions)) return [];
  const qs = mod.questions;

  // ✅ primary source: what the component persisted
  const persisted = responses?.[mod.id]?.__shownIds;
  if (Array.isArray(persisted) && persisted.length) {
    const idSet = new Set(persisted);
    return qs.filter((q) => idSet.has(q.id));
  }

  // optional secondary source: a static property on the module, if you use it
  if (Array.isArray(mod?.questionsShown) && mod.questionsShown.length > 0) {
    const shown = mod.questionsShown;
    if (typeof shown[0] === "object") return shown;
    const idSet = new Set(shown);
    return qs.filter((q) => idSet.has(q.id));
  }

  // fallback to the same randomise rule
  if (mod.randomise > 0) return qs.slice(0, mod.randomise);

  return qs;
};

// Recursively walk modules and collect ids of required-but-empty items
// Recursively walk modules and collect ids of required-but-empty items
const collectRequiredErrors = (
  modules,
  responses,
  signatures,
  isAssessableFn,
) => {
  const errors = [];

  const walk = (mod, parentId = null) => {
    // Skip if not assessable to the user
    if (!isAssessableFn(mod)) return;

    // If this is a container with children, walk only the visible/assessable children
    if (Array.isArray(mod?.children)) {
      mod.children.forEach((child) => walk(child, mod.id || parentId));
    }

    // dynamic_question_group — check only the rendered subset (already what UI shows)
    if (
      mod?.type === "dynamic_question_group" &&
      Array.isArray(mod.questions)
    ) {
      const renderedQs = resolveRenderedQuestions(mod, responses);

      renderedQs.forEach((q) => {
        if (!q?.required) return;

        // If question had its own visibility condition and is hidden, skip
        const vc = q.visibilityCondition;
        if (vc) {
          const target = responses?.[vc.moduleId];
          const visible =
            vc.operator === "equals"
              ? Array.isArray(target)
                ? target.includes(vc.value)
                : target === vc.value
              : vc.operator === "not_equals"
                ? Array.isArray(target)
                  ? !target.includes(vc.value)
                  : target !== vc.value
                : vc.operator === "contains"
                  ? Array.isArray(target) && target.includes(vc.value)
                  : true;
          if (!visible) return;
        }

        const ans = readAnswer(responses, q.id, mod.id);
        if (!isAnsweredByType(q.type, ans, signatures)) errors.push(q.id);
      });
    }

    // Leaf/group with its own id
    if (mod?.required) {
      // Try the normal location first
      let ans = readAnswer(responses, mod.id, parentId);

      // Fallback for modules that deliberately store under fixed keys
      if (typeof ans === "undefined") {
        const fk = fixedKeyForType(mod.type);
        if (fk) ans = readAnswer(responses, fk, parentId);
      }

      if (!isAnsweredByType(mod.type, ans, signatures)) errors.push(mod.id);
    }
  };

  (modules || []).forEach((m) => walk(m, null));
  return errors;
};

// ---- Correct-answers helpers ----
const normArray = (val) => {
  if (Array.isArray(val)) return val.map((v) => String(v).trim());
  if (val == null) return [];
  return [String(val).trim()];
};

const setsEqual = (a, b) => {
  const A = new Set(a),
    B = new Set(b);
  if (A.size !== B.size) return false;
  for (const x of A) if (!B.has(x)) return false;
  return true;
};

// Returns true iff user selected exactly the full set of correct answers (no more, no less)
const isExactlyCorrect = (userAnswer, correctAnswers) => {
  const ua = normArray(userAnswer);
  const ca = normArray(correctAnswers || []);
  return setsEqual(ua, ca);
};

function FormRenderer({ formId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [formStructure, setFormStructure] = useState(null);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [responses, setResponses] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [siteId, setSiteId] = useState(null);
  const [linkedResponse, setLinkedResponse] = useState(null); // New state for linkedResponse
  const [userId, setUserId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [formType, setFormType] = useState(null);
  const [parentCompanyId, setParentCompanyId] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [formStatus, setFormStatus] = useState(null); // e.g., 'draft', 'submitted'
  const [availableQuestionnaires, setAvailableQuestionnaires] = useState([]); // New state for questionnaires
  const [documentStatus, setDocumentStatus] = useState({}); // New state for document statuses
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [incorrectAnswers, setIncorrectAnswers] = useState([]);
  const [incorrectModules, setIncorrectModules] = useState([]); // To store which modules had incorrect answers
  const [hazards, setHazards] = useState([]);
  const [isHazardPopupOpen, setHazardPopupOpen] = useState(false);
  const [isZoneHazardPopupOpen, setZoneHazardPopupOpen] = useState(false);
  const [editedHazards, setEditedHazards] = useState([]);
  const [formData, setFormData] = useState({});
  const [signatures, setSignatures] = useState([]);
  const [submitError, setSubmitError] = useState("");

  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const isPreview = queryParams.get("isPreview") === "true";

  const jobIdParam = queryParams.get("jobId");

  const existingResponseId = queryParams.get("existing");
  const urlUserId = queryParams.get("userId");
  const urlCompanyId = queryParams.get("companyId");

  const matchesStatus = (module) => {
    const v = module.visibleWhenStatus;
    // a) no rule ⇒ show
    if (v == null) return true;

    // b) normalize into an array
    const arr = Array.isArray(v) ? v : [v];

    // c) if they included 0 ⇒ always show
    if (arr.includes(0)) return true;

    // d) otherwise only show when formStatus is in the list
    return arr.includes(formStatus);
  };

  useEffect(() => {
    if (urlUserId) {
      setUserId(urlUserId);
    }
  }, [urlUserId]);

  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // threshold so it only hides the hint just before true bottom
      const threshold = 50;
      const scrolledToBottom =
        window.innerHeight + window.pageYOffset >=
        document.documentElement.offsetHeight - threshold;
      setIsAtBottom(scrolledToBottom);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // init
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // **New State Declarations for Apply Success and Error**
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState("");

  const API_BASE_URL = "https://formbuilderapi.getforsite.com"; //process.env.REACT_APP_API_URL ||
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/forms/${formId}`);
        setFormStructure(response.data.formStructure);
        setRequiredDocuments(response.data.requiredDocuments || []);
        setSiteId(response.data.site_id);
        setParentCompanyId(response.data.company_id);
        setFormType(response.data.formStructure.formType);
        setFormStatus(response.data.status || "draft");

        // Always set linkedResponse if the URL contains a response parameter
        const urlResponse = queryParams.get("response");
        if (urlResponse) {
          setLinkedResponse(urlResponse);
          console.log("SetLinkedResponse", urlResponse);
        }

        // If no siteId from the API, check the URL for ?siteId and ?parentCompanyId
        if (!response.data.site_id) {
          const urlSiteId = queryParams.get("siteId");
          const urlParentCompanyId = queryParams.get("parentCompanyId");
          if (urlSiteId) {
            setSiteId(urlSiteId);
            setParentCompanyId(urlParentCompanyId);
          }
        }

        // Load editedHazards from localStorage if present
        const savedHazards = localStorage.getItem(`editedHazards_${formId}`);
        if (savedHazards) {
          setEditedHazards(JSON.parse(savedHazards));
        } else {
          setEditedHazards(response.data.formStructure.hazards || []);
        }
      } catch (error) {
        console.error("Error fetching form:", error);
        alert("An error occurred while fetching the form.");
      }
    };

    fetchForm();
  }, [formId, queryParams, API_BASE_URL]);

  // Fetch available questionnaires based on company_id
  useEffect(() => {
    if (!formStructure || !formStructure.company_id) {
      setAvailableQuestionnaires([]);
      return;
    }

    // const fetchQuestionnaires = async () => {
    //   try {
    //     const response = await axios.get(
    //       `${API_BASE_URL}/api/forms/document-checker`,
    //       {
    //         params: { company_id: formStructure.company_id },
    //       },
    //     );
    //     setAvailableQuestionnaires(response.data.questionnaires);
    //   } catch (error) {
    //     console.error("Error fetching questionnaires:", error);
    //     setAvailableQuestionnaires([]);
    //   }
    // };

    // fetchQuestionnaires();
  }, [formStructure]);

  

  // Check document statuses after formStructure and userId are set
  useEffect(() => {
    if (!formStructure || !userId || !availableQuestionnaires.length) return;

    const fetchDocumentStatus = async () => {
      const statuses = {};

      for (const module of formStructure.modules) {
        if (module.type === "document_checker") {
          const questionnaireId = module.selectedQuestionnaireId;
          if (questionnaireId) {
            try {
              const response = await axios.get(
                `${API_BASE_URL}/api/docs/document-checker/status`,
                {
                  params: {
                    questionnaire_id: questionnaireId,
                    user_id: userId,
                  },
                },
              );
              statuses[module.id] = response.data.hasDocument;
            } catch (error) {
              console.error("Error checking document status:", error);
              statuses[module.id] = false;
            }
          }
        }
      }

      setDocumentStatus(statuses);
    };

    fetchDocumentStatus();
  }, [formStructure, userId, availableQuestionnaires]);

  useEffect(() => {
    if (existingResponseId) {
      const fetchExistingResponse = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/forms/${formId}/responses/${existingResponseId}`,
          );
          const { responseData, status, signatures } = response.data;
          setResponses(responseData);
          setSignatures(signatures);

          if (status === 2) {
            setFormStatus(3);
          } else {
            setFormStatus(status);
          }
        } catch (error) {
          console.error("Error fetching existing response:", error);
          alert("An error occurred while fetching the existing response.");
        }
      };
      fetchExistingResponse();
    }
  }, [existingResponseId, formId]);

  useEffect(() => {
    if (jobIdParam && formStructure?.modules) {
      console.log("jobIdParam:", jobIdParam);
      // find every work_orders module
      const woIds = formStructure.modules
        .filter((m) => m.type === "work_orders")
        .map((m) => m.id);

      if (woIds.length) {
        setResponses((prev) => ({
          ...prev,
          // for each workOrders module, set its response to [jobId]
          ...woIds.reduce((acc, id) => {
            acc[id] = [Number(jobIdParam)];
            return acc;
          }, {}),
        }));
      }
    }
  }, [jobIdParam, formStructure]);

  // Avoid destructuring styles before formStructure is loaded
  const styles = formStructure?.styles || {};

  // Determine if fields are editable based on form status
  const isFieldEditable =
    formStatus === "draft" || formStatus === 0 || formStatus === 1;

  // Memoize handleChange to prevent unnecessary re-renders
  const handleChange = useCallback((key, value) => {
    const keys = key.split(".");
    if (keys.length === 2) {
      setResponses((prev) => ({
        ...prev,
        [keys[0]]: {
          ...(prev?.[keys[0]] ?? {}), // ✅ prevent spread of undefined
          [keys[1]]: value,
        },
      }));
    } else {
      setResponses((prev) => ({ ...prev, [key]: value }));
    }
  }, []);

  const handleAddTableRow = (key, columns) => {
    setResponses((prevResponses) => {
      const currentRows = prevResponses[key] || [];
      const newRow = columns.reduce((acc, col) => ({ ...acc, [col]: "" }), {});
      return { ...prevResponses, [key]: [...currentRows, newRow] };
    });
  };

  const handleDateTimeChange = (key, value) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [key]: value, // dayjs objects can be converted to ISO strings if needed
    }));
  };

  const handleRemoveTableRow = (key, rowIndex) => {
    setResponses((prevResponses) => {
      const currentRows = prevResponses[key] || [];
      const newRows = currentRows.filter((_, idx) => idx !== rowIndex);
      return { ...prevResponses, [key]: newRows };
    });
  };

  const handleTableCellChange = (key, rowIndex, column, value) => {
    setResponses((prevResponses) => {
      const currentRows = prevResponses[key] || [];
      const updatedRows = currentRows.map((row, idx) =>
        idx === rowIndex ? { ...row, [column]: value } : row,
      );
      return { ...prevResponses, [key]: updatedRows };
    });
  };

  useEffect(() => {
    if (editedHazards.length > 0) {
      localStorage.setItem(
        `editedHazards_${formId}`,
        JSON.stringify(editedHazards),
      );
    } else {
      localStorage.removeItem(`editedHazards_${formId}`);
    }
  }, [editedHazards, formId]);

  // Handler to apply changes to the backend
  const handleApplyChanges = async (updatedHazards) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/hazards/updateHazards`, // Ensure this is the correct endpoint
        { hazards: updatedHazards },
      );

      if (response.data.success) {
        setApplySuccess(true);
        // Removed alert for success message
        setEditedHazards([]); // Clear the editedHazards after applying
        localStorage.removeItem(`editedHazards_${formId}`); // Clear from localStorage if used
        fetchHazards(); // Re-fetch hazards if needed
        setHazardPopupOpen(false); // Close the HazardPopup
      } else {
        setApplyError("Failed to apply changes. Please try again.");
      }
    } catch (error) {
      console.error("Error applying changes:", error);
      setApplyError(
        error.response?.data?.error || "An unexpected error occurred.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (key, field, value) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [key]: {
        ...prevResponses[key],
        [field]: value,
      },
    }));
  };

  // 2) Replace your isModuleVisible with this
  const isModuleVisible = (module) => {
    if (isPreview) return true;
    // 1. hide by status first
    if (!matchesStatus(module)) return false;

    // 2. then your existing visibilityCondition logic
    if (!module.visibilityCondition) return true;
    const { moduleId, operator, value } = module.visibilityCondition;
    const target = responses[moduleId];

    switch (operator) {
      case "equals":
        return Array.isArray(target)
          ? target.includes(value)
          : target === value;
      case "not_equals":
        return Array.isArray(target)
          ? !target.includes(value)
          : target !== value;
      case "contains":
        return Array.isArray(target) && target.includes(value);
      default:
        return true;
    }
  };

  // Visible to the user and not approval-gated (unless preview)
  const isAssessable = (mod) =>
    isPreview
      ? true
      : matchesStatus(mod) && !mod.approvalRequired && isModuleVisible(mod);

  const confirmEmail = async () => {
    const emailKey = "email"; // Ensure the key is correct
    const email = responses[emailKey];
    if (!email || email.trim() === "") {
      setEmailError("Please enter an email address.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/confirm-email`, {
        email,
      });
      const { userId: fetchedUserId, companyId: fetchedCompanyId } =
        response.data;
      if (!urlUserId) {
        setUserId(fetchedUserId);
      }
      setCompanyId(fetchedCompanyId);
      setEmailConfirmed(true);
      setEmailError("");
      alert("Email confirmed successfully.");
    } catch (error) {
      console.error("Error confirming email:", error);
      setEmailError(
        error.response?.data?.error ||
          "An error occurred while confirming the email.",
      );
      setUserId(null);
      setCompanyId(null);
      setEmailConfirmed(false);
    }
  };

  // Handler to confirm email within User Details module

  const handleFileChange = (docId, file) => {
    if (!file) return;

    // allowed file extensions and MIME types
    const allowedExtensions = [".jpeg", ".jpg", ".png", ".pdf"];
    const allowedMimeTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/x-pdf",
      "application/acrobat",
      "applications/vnd.pdf",
      "text/pdf",
      "text/x-pdf",
    ];

    // get file extension
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    // validate extension and MIME type
    const isExtensionAllowed = allowedExtensions.includes(fileExtension);
    const isMimeTypeAllowed = allowedMimeTypes.includes(
      file.type.toLowerCase(),
    );

    if (!isExtensionAllowed || !isMimeTypeAllowed) {
      alert("Unsupported file type. Please upload a JPEG, PNG, or PDF file.");
      return;
    }

    // validate file size (max 10 MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("File size exceeds the 10MB limit.");
      return;
    }

    console.log(`File selected for docId ${docId}:`, file.name);

    // store the raw File object for later upload
    setUploadedFiles((prevFiles) => ({
      ...prevFiles,
      [docId]: file,
    }));

    // immediately update the UI: show “Change File” and filename
    setResponses((prevResponses) => ({
      ...prevResponses,
      [docId]: file, // so your renderButton can read responses[docId] truthiness
    }));
  };

  // Handler to upload one or more images with compression
  const handleImageUpload = async (docId, files) => {
    if (!files || files.length === 0) return;

    const allowedExtensions = [".jpeg", ".jpg", ".png", ".gif"];
    const allowedMimeTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];

    for (const file of files) {
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!allowedExtensions.includes(ext) || !allowedMimeTypes.includes(file.type.toLowerCase())) {
        alert(`"${file.name}" is an unsupported file type. Please upload JPEG, PNG, or GIF files only.`);
        return;
      }
    }

    const total = files.length;
    let done = 0;

    try {
      setLoading(true);
      setImageUploadProgress((prev) => ({ ...prev, [docId]: total > 1 ? `0 / ${total}` : "" }));

      const uploadedUrls = await Promise.all(
        Array.from(files).map(async (file) => {
          let finalFile = file;
          if (file.type.toLowerCase() !== "image/gif") {
            finalFile = await imageCompression(file, {
              maxSizeMB: 4,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            });
          }
          if (finalFile.size > 50 * 1024 * 1024) {
            throw new Error(`"${file.name}" exceeds the 50MB limit after compression.`);
          }
          const formData = new FormData();
          formData.append(`documents[${docId}]`, finalFile, file.name);
          const res = await axios.post(
            `${API_BASE_URL}/api/forms/${formId}/upload-documents`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          done += 1;
          if (total > 1) setImageUploadProgress((prev) => ({ ...prev, [docId]: `${done} / ${total}` }));
          return res.data.uploadedUrls[docId];
        }),
      );

      setResponses((prev) => {
        const current = prev[docId] || [];
        return { ...prev, [docId]: [...current, ...uploadedUrls] };
      });
      setSuccess(true);
    } catch (error) {
      console.error("Error uploading image(s):", error);
      alert("An error occurred while uploading one or more images.");
    } finally {
      setLoading(false);
      setImageUploadProgress((prev) => ({ ...prev, [docId]: null }));
    }
  };

  // Handler for removing images from responses
  const handleRemoveImageFromResponse = (key, imageIndex) => {
    setResponses((prevResponses) => {
      const updatedImages = prevResponses[key].filter(
        (_, idx) => idx !== imageIndex,
      );
      return { ...prevResponses, [key]: updatedImages };
    });
  };

  const handleSignatureChange = (moduleId, signatureData) => {
    setSignatures((prevSignatures) => {
      const existingIndex = prevSignatures.findIndex(
        (sig) => sig.module_id === moduleId,
      );
      if (existingIndex !== -1) {
        // Update existing signature
        const updatedSignatures = [...prevSignatures];
        updatedSignatures[existingIndex].signature_data = signatureData;
        return updatedSignatures;
      } else {
        // Add new signature
        return [
          ...prevSignatures,
          { module_id: moduleId, signature_data: signatureData },
        ];
      }
    });
  };

  // Handler for removing signatures

  const fetchHazards = async () => {
    if (editedHazards.length > 0) {
      setHazards(editedHazards);
    } else {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/hazards/getHazardRegister`,
          {
            params: { siteId }, // Ensure 'siteId' is properly defined
          },
        );
        setHazards(response.data);
        setEditedHazards(response.data); // Initialize the editedHazards state with the fetched data
      } catch (error) {
        console.error("Error fetching hazard register:", error);
      }
    }
  };

  // Helper function to shuffle array
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleTimeRangeChange = (key, field, value) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [key]: {
        ...prevResponses[key],
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    if (editedHazards.length > 0) {
      localStorage.setItem(
        `editedHazards_${formId}`,
        JSON.stringify(editedHazards),
      );
    } else {
      localStorage.removeItem(`editedHazards_${formId}`);
    }
  }, [editedHazards, formId]);

  const handleLocalSave = (newHazards) => {
    setEditedHazards(newHazards);
    // Optionally, persist to localStorage
    localStorage.setItem(`editedHazards_${formId}`, JSON.stringify(newHazards));
    setSuccess(true);
    alert("Changes saved locally.");
  };

  // New helper function to redirect to the custom success message
  const redirectToSuccessPage = () => {
    const successMessageHTML = `
      <html>
        <head>
          <style>
            body {
              font-family: Arial, sans-serif;
              background-color: #F0F0F0;
              text-align: center;
              padding: 50px;
            }
            .message {
              background-color: #FFD902;
              border: 1px solid #ddd;
              padding: 20px;
              display: inline-block;
              margin: 0 auto;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }
          </style>
        </head>
        <body>
          <div class="message">
            <h1>The form was submitted successfully.</h1>
          </div>
        </body>
      </html>
    `;
    const successMessage = new Blob([successMessageHTML], {
      type: "text/html",
    });
    const successUrl = URL.createObjectURL(successMessage);

    setTimeout(() => {
      window.location.href = successUrl;
    }, 1000);
  };

  const getSelectedServiceProviderId = React.useCallback(() => {
    const spModule = formStructure?.modules?.find(
      (m) => m.type === "service_provider_select",
    );
    if (!spModule) return null;

    const val = responses?.[spModule.id];

    // New shape: { selectedIds: [...] }
    if (val && Array.isArray(val.selectedIds) && val.selectedIds.length) {
      return String(val.selectedIds[0]);
    }
    // Legacy shapes
    if (Array.isArray(val) && val.length) return String(val[0]);
    if (typeof val === "string" || typeof val === "number") return String(val);

    return null;
  }, [formStructure, responses]);

  const getLinkedJobIds = () => {
    if (!formStructure?.modules) return [];

    // 1) From any work_orders modules
    const idsFromModules = formStructure.modules
      .filter((m) => m.type === "work_orders")
      .flatMap((m) => {
        const val = responses[m.id];
        return Array.isArray(val) ? val : val != null ? [val] : [];
      })
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));

    // 2) From jobId in the URL
    const idsFromUrl = jobIdParam ? [Number(jobIdParam)] : [];

    // 3) Merge + dedupe
    const all = [...idsFromModules, ...idsFromUrl];
    return Array.from(new Set(all));
  };

  const handleSubmit = async () => {
    if (formType === "Permit" && !siteId) {
      alert(
        "This form cannot be submitted without an associated site. Please contact your administrator.",
      );
      return;
    }
    setSubmitError("");

    // Compute effective company id for this submission
    const selectedServiceProviderId = getSelectedServiceProviderId();
    const effectiveCompanyId =
      formStructure?.formType === "Audit" && selectedServiceProviderId
        ? Number(selectedServiceProviderId) // backend likely expects a number
        : companyId;

    const newIncorrectAnswers = {};
    const newIncorrectModules = [];
    const customModulesIncorrect = [];
    let overrideStatus; // if defined, we include it in the payload
    let auditOverrideStatus; // 14 or 15 if thresholds are met

    // 1. Iterate through all modules to validate answers
    (formStructure.modules || []).forEach((module) => {
      if (!isAssessable(module)) return;

      // Standalone checkbox groups (single module questions)
      if (
        (module.type === "checkbox_group" ||
          module.type === "custom_checkbox_group") &&
        Array.isArray(module.correctResponses) &&
        module.correctResponses.length > 0
      ) {
        const userAnswers = responses[module.id];
        const ok = isExactlyCorrect(userAnswers, module.correctResponses);
        if (!ok) {
          if (module.customLogic?.action === "setStatusTo3") {
            customModulesIncorrect.push({
              id: module.id,
              questionLabel: module.label,
              message: module.customLogic.customMessage,
            });
          } else {
            newIncorrectAnswers[module.id] = normArray(userAnswers);
            newIncorrectModules.push({
              id: module.id,
              questionLabel: module.label,
            });
          }
        }
      }

      // Dynamic groups — only evaluate rendered (visible) questions
      if (
        module.type === "dynamic_question_group" &&
        Array.isArray(module.questions)
      ) {
        const evaluatedQuestions = resolveRenderedQuestions(module, responses);
        evaluatedQuestions.forEach((question) => {
          if (
            !Array.isArray(question?.correctResponses) ||
            question.correctResponses.length === 0
          ) {
            return;
          }

          // per-question visibility condition honored (same as required path)
          const vc = question.visibilityCondition;
          if (vc) {
            const target = responses?.[vc.moduleId];
            const visible =
              vc.operator === "equals"
                ? Array.isArray(target)
                  ? target.includes(vc.value)
                  : target === vc.value
                : vc.operator === "not_equals"
                  ? Array.isArray(target)
                    ? !target.includes(vc.value)
                    : target !== vc.value
                  : vc.operator === "contains"
                    ? Array.isArray(target) && target.includes(vc.value)
                    : true;
            if (!visible) return;
          }

          const rawAnswer =
            responses?.[question.id] ??
            (responses?.[module.id] && responses[module.id][question.id]);

          const ok = isExactlyCorrect(rawAnswer, question.correctResponses);
          if (!ok) {
            if (question.customLogic?.action === "setStatusTo3") {
              customModulesIncorrect.push({
                id: question.id,
                questionLabel: question.label,
                message: question.customLogic.customMessage,
              });
            } else {
              newIncorrectAnswers[question.id] = normArray(rawAnswer);
              newIncorrectModules.push({
                id: question.id,
                questionLabel: question.label,
              });
            }
          }
        });
      }
    });

    // 2. Handle incorrect answers
    if (Object.keys(newIncorrectAnswers).length > 0) {
      setIncorrectAnswers(newIncorrectAnswers);
      setIncorrectModules(newIncorrectModules);
      setModalOpen(true);
      return;
    } else if (customModulesIncorrect.length > 0) {
      const customAlertMessage = customModulesIncorrect
        .map((m) => m.message)
        .filter(Boolean)
        .join("\n");
      if (customAlertMessage) {
        alert(customAlertMessage);
      }
      overrideStatus = 3;
    }

    // 3. Basic validation: Ensure email is provided
    if (!responses.email && !responses.user_details?.email && !userId) {
      alert("Please provide your email address.");
      return;
    }

    // 4. Ensure that userId and companyId are set (email confirmed)
    if (!userId || !effectiveCompanyId) {
      setSubmitError("please confirm your email to submit");
      return;
    }

    // 5. Global "required" validation for ALL modules (nested or grouped)
    const validationErrors = collectRequiredErrors(
      formStructure.modules || [], // or visibleModules
      responses,
      signatures,
      isAssessable,
    );

    if (validationErrors.length > 0) {
      setValidationErrors(validationErrors);
      alert("Please answer all required questions.");
      return;
    }

    // 6. Validate required documents
    let missingDocs = [];
    let sanitizedDocuments = [];
    if (requiredDocuments.length > 0) {
      sanitizedDocuments = requiredDocuments.map((doc) => ({
        ...doc,
        required: Boolean(doc.required),
      }));
      missingDocs = sanitizedDocuments.filter(
        (doc) => doc.required && !uploadedFiles[doc.id],
      );
    }

    if (missingDocs.length > 0) {
      const missingNames = missingDocs.map((doc) => doc.name).join(", ");
      alert(
        `Please upload the following required document(s): ${missingNames}`,
      );
      return;
    }

    // 7. Compute the final payload status
    let payloadStatus;
 
    if (formStructure?.formType === "Audit") {
      // Use the unified evaluator (handles mandatory-fail + threshold + scoring)
      const finalEval = evaluateCombinedAuditStatus(formStructure, responses);
 
      if (finalEval.status === 15 || finalEval.status === 14) {
        payloadStatus = finalEval.status;
      } else if (finalEval.status === 13) {
        payloadStatus = 13; // PASS
      } else {
        payloadStatus = 8; // no thresholds / scoring enabled
      }
 
      // Store the score percentage in the response data if scoring is enabled
      if (finalEval.scoringEnabled && finalEval.percent !== null) {
        // Merge into finalResponses (defined just below)
        // We attach it here so the backend persists it with the response data.
        // NOTE: assign before finalResponses is used in the payload build.
        responses._auditScore = finalEval.percent;
      }
    } else {
      // Non-Audit: preserve existing override/approval logic
      if (typeof auditOverrideStatus !== "undefined" && auditOverrideStatus !== null) {
        payloadStatus = auditOverrideStatus;
      } else if (typeof overrideStatus !== "undefined") {
        payloadStatus = overrideStatus;
      } else if (formStructure.approvalRequired) {
        payloadStatus = undefined;
      } else {
        payloadStatus = undefined;
      }
    }

    // 8. Promote email_dropdown responses
    let emailArray = [];
    if (formStructure && Array.isArray(formStructure.modules)) {
      const emailDropdownModules = formStructure.modules.filter(
        (m) => m.type === "email_dropdown",
      );
      if (emailDropdownModules.length === 1) {
        const value = responses[emailDropdownModules[0].id] || [];
        emailArray = Array.isArray(value) ? value : value ? [value] : [];
      } else if (emailDropdownModules.length > 1) {
        emailArray = emailDropdownModules.reduce((acc, module) => {
          const value = responses[module.id];
          if (Array.isArray(value)) {
            return acc.concat(value);
          } else if (value) {
            acc.push(value);
          }
          return acc;
        }, []);
      }
    }

    // 9. INJECT work_orders SHIM if needed
    const hasWO = formStructure.modules.some((m) => m.type === "work_orders");
    const finalResponses = {
      ...responses,
      ...(jobIdParam && !hasWO ? { work_orders: [Number(jobIdParam)] } : {}),
    };

    setLoading(true);
    try {
      // a) Upload any files
      const allUploadedDocIds = Object.keys(uploadedFiles);
      let uploadedDocumentUrls = {};

      if (allUploadedDocIds.length > 0) {
        const formDataObj = new FormData();
        allUploadedDocIds.forEach((docId) => {
          const file = uploadedFiles[docId];
          formDataObj.append(`documents[${docId}]`, file, file.name);
        });

        const uploadResponse = await axios.post(
          `${API_BASE_URL}/api/forms/${formId}/upload-documents`,
          formDataObj,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );
        uploadedDocumentUrls = uploadResponse.data.uploadedUrls;
      }

      // b) Prepare signatures payload
      const signaturesPayload = signatures.map((sig) => ({
        module_id: sig.module_id,
        signature_data: sig.signature_data,
      }));

      // c) Prepare & send the final submission payload
      let payload;
      let response;
      if (existingResponseId) {
        payload = {
          data: {
            ...finalResponses,
            documents: {
              ...(finalResponses.documents || {}),
              ...uploadedDocumentUrls,
            },
            hazards: editedHazards,
            completed_with_forms: Array.isArray(linkedResponse)
              ? linkedResponse
              : [linkedResponse],
            emails: emailArray,
          },
          signatures: signaturesPayload,
          ...(typeof payloadStatus !== "undefined"
            ? { status: payloadStatus }
            : {}),
        };

        response = await axios.put(
          `${API_BASE_URL}/api/forms/${formId}/responses/${existingResponseId}`,
          payload,
        );
      } else {
        payload = {
          type: formStructure.type,
          data: {
            ...finalResponses,
            documents: { ...uploadedDocumentUrls },
            hazards: editedHazards,
            completed_with_forms: Array.isArray(linkedResponse)
              ? linkedResponse
              : [linkedResponse],
            emails: emailArray,
          },
          userId,
          companyId: effectiveCompanyId,
          parentCompanyId,
          doctype: formType,
          signatures: signaturesPayload,
          site_id: siteId || null,
          ...(typeof payloadStatus !== "undefined"
            ? { status: payloadStatus }
            : {}),
        };

        response = await axios.post(
          `${API_BASE_URL}/api/forms/${formId}/responses`,
          payload,
        );
      }

      setUploadedUrl(response.data.uploadedUrl);
      setSuccess(true);
      redirectToSuccessPage();
    } catch (error) {
      if (error.response?.data?.error) {
        alert(`Submission failed: ${error.response.data.error}`);
      } else {
        alert("An unexpected error occurred while submitting the form.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInProgress = async () => {
    // Guard: Permit forms must always have a site
    if (formType === "Permit" && !siteId) {
      alert(
        "This form cannot be submitted without an associated site. Please contact your administrator.",
      );
      return;
    }

    // 1. Basic validation: Ensure email is provided
    if (!responses.email && !responses.user_details?.email && !userId) {
      alert("Please provide your email address.");
      console.log("Email not provided");
      return;
    }

    // 2. Ensure that userId and companyId are set (email confirmed)
    if (!userId || !companyId) {
      alert("Please confirm your email before submitting the form.");
      console.log("userId or companyId not set");
      return;
    }

    // 3. Validate required documents only if there are any
    let missingDocs = [];
    let sanitizedDocuments = [];
    if (requiredDocuments.length > 0) {
      // Sanitize 'required' to boolean to prevent truthy/falsy issues
      sanitizedDocuments = requiredDocuments.map((doc) => ({
        ...doc,
        required: Boolean(doc.required),
      }));

      // Identify missing required documents
      missingDocs = sanitizedDocuments.filter(
        (doc) => doc.required && !uploadedFiles[doc.id],
      );
    }

    console.log("Sanitized Required Documents:", sanitizedDocuments);
    sanitizedDocuments.forEach((doc) => {
      console.log(`Doc ID: ${doc.id}, Required: ${doc.required}`);
    });

    console.log("Uploaded Files:", uploadedFiles);
    console.log("Missing Required Documents:", missingDocs);

    // 4. If there are missing required documents, alert the user and halt submission
    if (missingDocs.length > 0) {
      const missingNames = missingDocs.map((doc) => doc.name).join(", ");
      alert(
        `Please upload the following required document(s): ${missingNames}`,
      );
      console.log("Missing required documents:", missingNames);
      return;
    }

    // 5. Proceed to upload any uploaded files (required or optional)
    const allUploadedDocIds = Object.keys(uploadedFiles);
    console.log("All uploaded document IDs:", allUploadedDocIds);

    if (allUploadedDocIds.length === 0) {
      console.log("No uploaded documents to process.");
    }

    setLoading(true); // Show loading indicator

    try {
      let uploadedDocumentUrls = {};

      if (allUploadedDocIds.length > 0) {
        // a. Create FormData for all uploaded files
        const formData = new FormData();
        allUploadedDocIds.forEach((docId) => {
          const file = uploadedFiles[docId];
          formData.append(`documents[${docId}]`, file, file.name);
          console.log(`Appending file for docId: ${docId}`, file.name);
        });

        // b. Upload documents to backend
        console.log("Uploading documents to backend...");
        const uploadResponse = await axios.post(
          `${API_BASE_URL}/api/forms/${formId}/upload-documents`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        console.log("Upload response:", uploadResponse.data);

        // c. Extract uploaded URLs from response
        uploadedDocumentUrls = uploadResponse.data.uploadedUrls; // { documentId: url }
        console.log("Uploaded Document URLs:", uploadedDocumentUrls);
      }

      // 6. Prepare signatures payload
      const signaturesPayload = signatures.map((sig) => ({
        module_id: sig.module_id,
        signature_data: sig.signature_data,
      }));

      // 7. Compute all linked job IDs (from work_orders modules + URL + any existing top-level work_orders)
      const moduleJobIds = (formStructure?.modules || [])
        .filter((m) => m.type === "work_orders")
        .flatMap((m) => {
          const val = responses[m.id];
          if (Array.isArray(val)) return val;
          if (val == null) return [];
          return [val];
        })
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v));

      const urlJobId = jobIdParam ? Number(jobIdParam) : null;

      const topLevelJobIds = Array.isArray(responses.work_orders)
        ? responses.work_orders
            .map((v) => Number(v))
            .filter((v) => Number.isFinite(v))
        : [];

      const allJobIds = [
        ...moduleJobIds,
        ...topLevelJobIds,
        ...(urlJobId ? [urlJobId] : []),
      ].filter((v) => Number.isFinite(v));

      const uniqueJobIds = Array.from(new Set(allJobIds));

      // 8. Build finalResponses with top-level work_orders so backend can link jobs
      const finalResponses = {
        ...responses,
        ...(uniqueJobIds.length ? { work_orders: uniqueJobIds } : {}),
      };

      // 9. Prepare the final submission payload
      let payload;
      let response;

      if (existingResponseId) {
        // Update existing response
        payload = {
          data: {
            ...finalResponses,
            documents: {
              // Merge existing documents with new ones
              ...(finalResponses.documents || {}),
              ...uploadedDocumentUrls,
            },
            hazards: editedHazards, // Include editedHazards here
          },
          signatures: signaturesPayload,
          // Force status to 1 for in-progress
          status: 1,
        };

        console.log("Updating existing response with payload:", payload);

        response = await axios.put(
          `${API_BASE_URL}/api/forms/${formId}/responses/${existingResponseId}`,
          payload,
        );
      } else {
        // Create new response
        payload = {
          type: formStructure.type, // Include the form name/type
          data: {
            ...finalResponses,
            documents: { ...uploadedDocumentUrls }, // Store document URLs under 'documents'
            hazards: editedHazards, // Include editedHazards here
          }, // User responses with documents
          userId,
          companyId,
          parentCompanyId,
          doctype: formType,
          signatures: signaturesPayload,
          site_id: siteId || null,
          // Force status to 1 for in-progress
          status: 1,
        };

        console.log("Submitting new response with payload:", payload);

        response = await axios.post(
          `${API_BASE_URL}/api/forms/${formId}/responses`,
          payload,
        );
      }

      console.log(`Form submission successful!`);
      setUploadedUrl(response.data.uploadedUrl);
      setSuccess(true);
      // Redirect to the custom success page instead of using an alert
      redirectToSuccessPage();
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(`Submission failed: ${error.response.data.error}`);
      } else {
        alert("An unexpected error occurred while submitting the form.");
      }
    } finally {
      setLoading(false);
    }
  };

  // -- Predicted audit outcome (Submit only visual) --
  const isAuditForm = formStructure?.formType === "Audit";
 
  // Live evaluation — reruns on every response change
  const auditEval = React.useMemo(
    () =>
      isAuditForm
        ? evaluateCombinedAuditStatus(formStructure, responses)
        : null,
    [isAuditForm, formStructure, responses],
  );
 
  // Convenience flags
  const thresholdEnabled = Boolean(formStructure?.threshold?.enabled);
  const scoringEnabled   = Boolean(formStructure?.scoring?.enabled);
 
  const auditBanner  = React.useMemo(
    () => (auditEval ? buildAuditBanner(auditEval) : null),
    [auditEval],
  );
 
  const countsText = React.useMemo(() => {
    if (!thresholdEnabled || !auditEval) return "";
    // Re-derive crit counts for display (existing helper still present in this file)
    const counts = countCritQuestionHits(formStructure.modules || [], responses);
    return `${counts.High} High, ${counts.Medium} Medium, ${counts.Low} Low`;
  }, [thresholdEnabled, formStructure, responses, auditEval]);

  // Pop-up to display validation errors

  // Section renderer: styled header bar + content (matches builder UI)
  const renderSectionModule = (module, key) => {
    const sectionTitle = module.label || module.title || "Section";
    return (
      <Box
        key={key}
        sx={{
          border: "2px solid #c7d2fe",
          borderRadius: 2,
          overflow: "hidden",
          my: 2,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            backgroundColor: "#eef2ff",
            borderBottom: "1.5px solid #c7d2fe",
          }}
        >
          <Box
            component="span"
            sx={{ color: "#4f46e5", fontSize: 16, lineHeight: 1, display: "flex", alignItems: "center" }}
          >
            ◈
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#4f46e5", fontSize: "0.875rem" }}
          >
            {sectionTitle}
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {module.children &&
            module.children.map((child) => renderModule(child, child.id))}
        </Box>
      </Box>
    );
  };

  // Helper function to render nested modules
  const renderModule = (module, key) => {
    if (!isModuleVisible(module)) {
      return null; // Do not render the module if visibility condition is not met
    }

    const isMulti = module.multiCompanies === true;

    switch (module.type) {
      case "banner":
        return (
          <Box
            key={key}
            sx={{
              backgroundColor: styles.bannerColor || module.color,
              padding: 2,
              borderRadius: 1,
              marginY: 2,
            }}
          >
            {module.children &&
              module.children.map((child, idx) =>
                renderModule(child, `${key}-child-${idx}`),
              )}
          </Box>
        );
      case "section":
        return renderSectionModule(module, key);

      case "user_multi_select":
        return (
          <UserMultiSelectRenderer
            key={key}
            module={module}
            companyId={companyId}
            responses={responses}
            onChange={(id, value) => handleChange("linked_users", value)}
            isEditable={isFieldEditable}
          />
        );

      case "contractorClosureSection":
        return (
          <Box
            key={key}
            sx={{
              border: `3px dashed ${module.color}`,
              padding: 2,
              borderRadius: 1,
              marginY: 2,
            }}
          >
            {module.children &&
              module.children.map((child) => renderModule(child, child.id))}
          </Box>
        );
      case "adminClosureSection":
        return (
          <Box
            key={key}
            sx={{
              border: `3px dashed ${module.color}`,
              padding: 2,
              borderRadius: 1,
              marginY: 2,
            }}
          >
            {module.children &&
              module.children.map((child) => renderModule(child, child.id))}
          </Box>
        );

      case "header":
        return (
          <Typography
            key={key}
            variant={styles.header?.fontSize || "h5"}
            gutterBottom
            sx={{
              color: styles.header?.color || "inherit",
              fontFamily: styles.header?.fontFamily || "inherit",
            }}
          >
            {module.text}
          </Typography>
        );

      case "subheader":
        return (
          <Typography
            key={key}
            variant={styles.subheader?.fontSize || "h6"}
            gutterBottom
            sx={{
              color: styles.subheader?.color || "inherit",
              fontFamily: styles.subheader?.fontFamily || "inherit",
            }}
          >
            {module.text}
          </Typography>
        );

      case "site_information_viewer":
        return (
          <SiteInformationViewer
            key={key}
            siteId={siteId}
            responses={responses}
            handleChange={handleChange}
          />
        );
      case "roofSiteInformationModule":
        return (
          <RoofPlanViewer
            key={key}
            siteId={siteId}
            responses={responses}
            handleChange={handleChange}
          />
        );
      case "hazard_register":
        return (
          <Box key={key} marginY={2}>
            <Typography variant="h6">{module.label}</Typography>
            <Button
              variant="outlined"
              onClick={() => {
                fetchHazards();
                setHazardPopupOpen(true);
              }}
            >
              View Hazard Register
            </Button>
          </Box>
        );
      case "video_embed": {
        // Expected fields on module:
        // - url (required): string
        // - title (optional): string
        // - caption (optional): string
        // - poster (optional): string (for direct video files)
        // - autoplay, muted, loop (optional): booleans
        // - aspectRatio (optional): number, default 16/9
        const url = (module.url || "").trim();
        if (!url) return null;

        const aspect =
          Number(module.aspectRatio) > 0 ? Number(module.aspectRatio) : 16 / 9;
        const { kind, src, href } = buildEmbed(url, {
          autoplay: !!module.autoplay,
          muted: !!module.muted,
          loop: !!module.loop,
        });

        return (
          <Box key={key} my={2}>
            {module.title && (
              <Typography variant="h6" gutterBottom>
                {module.title}
              </Typography>
            )}

            {kind === "iframe" && (
              <ResponsiveFrame aspect={aspect}>
                <iframe
                  src={src}
                  title={module.title || "Embedded video"}
                  style={{ width: "100%", height: "100%", border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </ResponsiveFrame>
            )}

            {kind === "video" && (
              <Box>
                <video
                  src={src}
                  style={{ width: "100%", borderRadius: 8, display: "block" }}
                  controls
                  playsInline
                  autoPlay={!!module.autoplay}
                  muted={!!module.muted}
                  loop={!!module.loop}
                  poster={module.poster || undefined}
                  preload="metadata"
                />
              </Box>
            )}

            {kind === "link" && (
              <Alert severity="info" sx={{ my: 1 }}>
                This link can’t be embedded.{" "}
                <Button
                  href={href}
                  target="_blank"
                  rel="noopener"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  Open video
                </Button>
              </Alert>
            )}

            {module.caption && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {module.caption}
              </Typography>
            )}
          </Box>
        );
      }
      case "roofZoneHazardRegisterModule":
        return (
          <Box key={key} my={2} display="flex" justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => setZoneHazardPopupOpen(true)}
              sx={{
                borderColor: "#d00000",
                color: "#d00000",
                "&:hover": {
                  borderColor: "#a10000",
                  backgroundColor: "rgba(208,0,0,0.08)",
                },
              }}
            >
              View Roof Zone Hazard Register
            </Button>
          </Box>
        );

      case "document_checker":
        const documentName = module.documentName || "Unknown Document";
        return (
          <Box key={key} marginY={2} display="flex" alignItems="center">
            <Typography variant="body1">
              Checking if you have a {documentName} document:
            </Typography>
            {documentStatus[module.id] ? (
              <CheckCircleIcon color="success" sx={{ marginLeft: 1 }} />
            ) : (
              <CancelIcon color="error" sx={{ marginLeft: 1 }} />
            )}
          </Box>
        );

      case "image":
        return (
          <Box key={key} marginY={2} textAlign="center">
            <img
              src={module.url}
              alt={module.alt_text}
              style={{ maxWidth: "60%" }}
            />
          </Box>
        );
      case "dynamic_question_group":
        return (
          <DynamicQuestionGroupComponent
            key={key}
            module={module}
            responses={responses}
            handleChange={handleChange}
            isEditable={isFieldEditable}
            validationErrors={validationErrors}
          />
        );

      case "paragraph":
        return (
          <div
            key={key}
            style={{ marginBottom: "16px" }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(module.content || ""),
            }}
          />
        );

      case "email_dropdown":
        return (
          <FormControl
            key={module.id}
            fullWidth
            margin="normal"
            required={module.required}
            disabled={!isFieldEditable}
          >
            <InputLabel>{module.label}</InputLabel>
            <Select
              multiple={true}
              value={responses[module.id] || []}
              onChange={(e) => handleChange(module.id, e.target.value)}
              renderValue={(selected) =>
                Array.isArray(selected) ? selected.join(", ") : selected
              }
            >
              {module.options.map((option, idx) => (
                <MenuItem key={idx} value={option.value}>
                  <Checkbox
                    checked={responses[module.id]?.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
            {module.required &&
              (!responses[module.id] || responses[module.id].length === 0) && (
                <FormHelperText error>
                  Please select at least one email.
                </FormHelperText>
              )}
          </FormControl>
        );

      case "rectangle_link_button":
        return (
          <RectangleLinkButton
            key={module.id}
            title={module.label}
            url={module.url}
            buttonColour={styles.buttonColor}
            openInNewTab={module.openInNewTab}
          />
        );

      case "long_answer":
        // Long-answer labels are often full instructions/observations. A floating
        // MUI label collapses to a single line and truncates with an ellipsis, so
        // render the label as a wrapping block above the field instead.
        return (
          <Box key={key} sx={{ mt: 2, mb: 2 }}>
            <FormLabel
              sx={{
                display: "block",
                mb: 0.75,
                fontWeight: 500,
                color: "text.primary",
                fontSize: "0.9rem",
                whiteSpace: "normal",
                lineHeight: 1.4,
              }}
            >
              {module.label}
              {module.required && (
                <Typography component="span" color="error" sx={{ ml: 0.25 }}>
                  *
                </Typography>
              )}
            </FormLabel>
            <TextField
              value={responses[key] || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              fullWidth
              required={module.required}
              multiline
              rows={4}
              InputProps={{
                readOnly: !isFieldEditable,
              }}
              disabled={!isFieldEditable}
            />
          </Box>
        );

      case "short_answer":
        return (
          <TextField
            key={key}
            label={module.label}
            value={responses[key] || ""}
            onChange={(e) => handleChange(key, e.target.value)}
            fullWidth
            required={module.required}
            margin="normal"
            InputProps={{
              readOnly: !isFieldEditable,
            }}
            disabled={!isFieldEditable}
            sx={{ marginBottom: 2 }}
          />
        );

      case "user_multi_select":
        return (
          <UserMultiSelectRenderer
            key={key}
            module={module}
            companyId={companyId} // companyId is set from confirmEmail/fetchEmail
            responses={responses}
            onChange={(updatedModule) => {
              // Update the module’s selectedUserIds as needed.
              setResponses((prev) => ({
                ...prev,
                [module.id]: updatedModule.selectedUserIds,
              }));
            }}
            isEditable={isFieldEditable}
          />
        );
      case "service_provider_select":
        return (
          <ServiceProviderSelectRenderer
            key={key}
            module={module}
            companyId={parentCompanyId}
            responses={responses}
            onChange={(updatedModule) => {
              // updatedModule looks like: { selectedIds: [...], tags?: [...] }
              setResponses((prev) => ({
                ...prev,
                [module.id]: {
                  ...(prev[module.id] && typeof prev[module.id] === "object"
                    ? prev[module.id]
                    : {}),
                  ...updatedModule, // <-- keep both selectedIds and tags
                },
              }));
            }}
            isEditable={isFieldEditable}
          />
        );

      case "checkbox":
        return (
          <Box
            key={key}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            marginBottom={2} // Adds margin below the module for separation
          >
            {/* Question Label on the Left */}
            <Typography variant="body1">{module.label}</Typography>

            {/* Checkbox on the Right with a small gap */}
            <Checkbox
              checked={responses[key] || false}
              onChange={(e) => handleChange(key, e.target.checked)}
              disabled={!isFieldEditable}
              color="primary"
            />
          </Box>
        );

        case "inspection_item": {
  const INSP_CRIT_COLORS = { High: "#d00000", Medium: "#ff9a02", Low: "#00cfaa" };
  const inspNormCrit = (ac) => ({
    High: Array.isArray(ac?.High) ? ac.High : [],
    Medium: Array.isArray(ac?.Medium) ? ac.Medium : [],
    Low: Array.isArray(ac?.Low) ? ac.Low : [],
  });
  const inspGetLevel = (opt) => {
    const { High, Medium, Low } = inspNormCrit(module?.auditCriticality);
    if (High.includes(opt)) { return "High"; }
    if (Medium.includes(opt)) { return "Medium"; }
    if (Low.includes(opt)) { return "Low"; }
    return null;
  };

  const inspOptions = module.options || ["Safe", "At Risk", "N/A"];
  const rawVal = responses[key];
  const inspCurrent = rawVal && typeof rawVal === "object" && !Array.isArray(rawVal)
    ? rawVal
    : { selection: typeof rawVal === "string" ? rawVal : null, notes: "", media: [] };
  const { selection: inspSel, notes: inspNotes, media: inspMedia } = inspCurrent;
  const inspMediaList = Array.isArray(inspMedia) ? inspMedia : [];

  // Local state isn't available in renderModule so we use a wrapper component
  // The simplest approach: import InspectionItemField at the top and use it here.
  // If you've imported InspectionItemField at the top of FormRenderer:
  return (
    <InspectionItemField
      key={key}
      module={module}
      value={responses[key]}
      isEditable={isFieldEditable}
      onChange={handleChange}
      fieldKey={key}
      formId={formId}
    />
  );
}

      case "audit_checkbox_group": {
        const selected = responses[module.id] || [];

        // single-select with checkboxes (array with 0/1 value)
        if (module.multiple === false) {
          return (
            <FormControl
              component="fieldset"
              key={module.id}
              margin="normal"
              required={module.required}
              disabled={!isFieldEditable}
              sx={{ width: "100%", mb: 2 }}
            >
              <FormLabel component="legend">{module.label}</FormLabel>

              {/* Vertical list */}
              <FormGroup
                sx={{
                  gap: 0.5,
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                {module.options.map((option, idx) => {
                  const level = getOptionCritLevel(module, option);
                  return (
                    <FormControlLabel
                      key={idx}
                      control={
                        <Checkbox
                          checked={selected.includes(option)}
                          onChange={(e) =>
                            handleChange(
                              module.id,
                              e.target.checked ? [option] : [],
                            )
                          }
                          disabled={!isFieldEditable}
                        />
                      }
                      label={
                        <OptionLabelWithPill label={option} level={level} />
                      }
                    />
                  );
                })}
              </FormGroup>

              {module.required && selected.length === 0 && (
                <FormHelperText error>Please select one option</FormHelperText>
              )}
            </FormControl>
          );
        }

        // multi-select
        return (
          <FormControl
            component="fieldset"
            key={module.id}
            margin="normal"
            required={module.required}
            disabled={!isFieldEditable}
            sx={{ width: "100%", mb: 2 }}
          >
            <FormLabel component="legend">{module.label}</FormLabel>

            {/* Vertical list */}
            <FormGroup
              sx={{
                gap: 0.5,
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              {module.options.map((option, idx) => {
                const level = getOptionCritLevel(module, option);
                return (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={selected.includes(option)}
                        onChange={(e) => {
                          let newValue = selected;
                          if (e.target.checked)
                            newValue = [...selected, option];
                          else
                            newValue = selected.filter((val) => val !== option);
                          handleChange(module.id, newValue);
                        }}
                        disabled={!isFieldEditable}
                      />
                    }
                    label={<OptionLabelWithPill label={option} level={level} />}
                  />
                );
              })}
            </FormGroup>

            {module.required && selected.length === 0 && (
              <FormHelperText error>
                Please select at least one option
              </FormHelperText>
            )}
          </FormControl>
        );
      }

      case "image_upload":
        const uploadedImages = responses[key] || [];
        return (
          <Box key={key} marginY={2}>
            <Typography variant="subtitle1">{module.label}</Typography>
            {uploadedImages.length > 0 && (
              <ImageList cols={3} gap={8} sx={{ marginTop: 1 }}>
                {uploadedImages.map((img, imgIndex) => (
                  <ImageListItem key={imgIndex}>
                    <img
                      src={img}
                      alt={`Uploaded ${imgIndex + 1}`}
                      loading="lazy"
                    />
                    {isFieldEditable && (
                      <ImageListItemBar
                        position="top"
                        actionIcon={
                          <IconButton
                            sx={{ color: "white" }}
                            aria-label={`delete image ${imgIndex + 1}`}
                            onClick={() =>
                              handleRemoveImageFromResponse(key, imgIndex)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      />
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
            )}
            {isFieldEditable && (
              <>
                <Button
                  variant="outlined"
                  startIcon={
                    imageUploadProgress[key] != null
                      ? <CircularProgress size={16} />
                      : <CloudUploadIcon />
                  }
                  component="label"
                  disabled={imageUploadProgress[key] != null}
                  sx={{ marginTop: uploadedImages.length > 0 ? 1 : 0 }}
                >
                  {imageUploadProgress[key]
                    ? `Uploading ${imageUploadProgress[key]}…`
                    : imageUploadProgress[key] === ""
                    ? "Uploading…"
                    : "Upload Images"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        handleImageUpload(key, e.target.files);
                      }
                    }}
                  />
                </Button>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ marginTop: 0.5 }}
                >
                  {uploadedImages.length}/{module.imageLimit} images uploaded.
                </Typography>
              </>
            )}
            <Divider sx={{ marginTop: 2, borderColor: "grey.300" }} />
          </Box>
        );

      case "work_orders":
        return (
          <WorkOrdersSelector
            key={key}
            module={module}
            responses={responses}
            handleChange={handleChange}
            isEditable={isFieldEditable}
            companyId={companyId} // ← pass down state
            qId={formId} // ← your real qId
          />
        );

      case "dropdown_select":
        return (
          <FormControl
            key={module.id}
            fullWidth
            margin="normal"
            required={module.required}
            disabled={!isFieldEditable}
          >
            <InputLabel>{module.label}</InputLabel>
            <Select
              multiple={module.multiple}
              value={responses[module.id] || (module.multiple ? [] : "")}
              onChange={(e) => handleChange(module.id, e.target.value)}
              renderValue={(selected) => {
                // always treat selected as an array
                const selectedArray = Array.isArray(selected)
                  ? selected
                  : [selected];
                // map each value to its label
                const labels = selectedArray
                  .map(
                    (val) =>
                      module.options.find((opt) => opt.value === val)?.label,
                  )
                  .filter(Boolean);
                return labels.join(", ");
              }}
            >
              {module.options.map((option, idx) => (
                <MenuItem key={idx} value={option.value}>
                  {module.multiple && (
                    <Checkbox
                      checked={responses[module.id]?.includes(option.value)}
                    />
                  )}
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
            {module.required &&
              (!responses[module.id] ||
                (Array.isArray(responses[module.id]) &&
                  responses[module.id].length === 0)) && (
                <FormHelperText error>
                  Please select at least one option
                </FormHelperText>
              )}
          </FormControl>
        );

      case "custom_document_uploader":
        return (
          <CustomDocumentUploader
            key={key}
            module={module}
            uploadedFiles={uploadedFiles}
            handleFileChange={handleFileChange}
            isEditable={isFieldEditable}
          />
        );

      case "time_range":
        const timeRange = responses[key] || { startTime: null, endTime: null };
        return (
          <Box key={key} marginY={2}>
            <Typography variant="h6">{module.label}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TimePicker
                  label="Start Time"
                  value={timeRange.startTime}
                  onChange={(newValue) =>
                    handleTimeRangeChange(key, "startTime", newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required={module.required}
                      InputProps={{
                        readOnly: !isFieldEditable,
                      }}
                      disabled={!isFieldEditable}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label="End Time"
                  value={timeRange.endTime}
                  onChange={(newValue) =>
                    handleTimeRangeChange(key, "endTime", newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required={module.required}
                      InputProps={{
                        readOnly: !isFieldEditable,
                      }}
                      disabled={!isFieldEditable}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case "date_range":
        const dateRange = responses[key] || { startDate: null, endDate: null };
        return (
          <Box key={key} marginY={2}>
            <Typography variant="h6">{module.label}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.startDate}
                  inputFormat="DD/MM/YYYY"
                  onChange={(newValue) =>
                    handleDateRangeChange(key, "startDate", newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required={module.required}
                      InputProps={{
                        readOnly: !isFieldEditable,
                      }}
                      disabled={!isFieldEditable}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={dateRange.endDate}
                  inputFormat="DD/MM/YYYY"
                  onChange={(newValue) =>
                    handleDateRangeChange(key, "endDate", newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required={module.required}
                      InputProps={{
                        readOnly: !isFieldEditable,
                      }}
                      disabled={!isFieldEditable}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case "checkbox_group":
      case "custom_checkbox_group": {
        const selected = responses[module.id] || [];

        // single‐select with checkboxes
        if (module.multiple === false) {
          return (
            <FormControl
              component="fieldset"
              key={module.id}
              margin="normal"
              required={module.required}
              disabled={!isFieldEditable}
              sx={{ width: "100%", marginBottom: 2 }}
            >
              <FormLabel component="legend">{module.label}</FormLabel>
              <FormGroup row sx={{ gap: 1 }}>
                {module.options.map((option, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={selected.includes(option)}
                        onChange={(e) =>
                          handleChange(
                            module.id,
                            e.target.checked ? [option] : [],
                          )
                        }
                        disabled={!isFieldEditable}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
              {module.required && selected.length === 0 && (
                <FormHelperText error>Please select one option</FormHelperText>
              )}
            </FormControl>
          );
        }

        // multi‐select (unchanged)
        return (
          <FormControl
            component="fieldset"
            key={module.id}
            margin="normal"
            required={module.required}
            disabled={!isFieldEditable}
            sx={{ width: "100%", marginBottom: 2 }}
          >
            <FormLabel component="legend">{module.label}</FormLabel>
            <FormGroup row sx={{ gap: 1 }}>
              {module.options.map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      checked={selected.includes(option)}
                      onChange={(e) => {
                        let newValue = selected;
                        if (e.target.checked) {
                          newValue = [...selected, option];
                        } else {
                          newValue = selected.filter((val) => val !== option);
                        }
                        handleChange(module.id, newValue);
                      }}
                      disabled={!isFieldEditable}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
            {module.required && selected.length === 0 && (
              <FormHelperText error>
                Please select at least one option
              </FormHelperText>
            )}
          </FormControl>
        );
      }

      case "datetime":
        return (
          <Box key={key} marginY={2}>
            <TextField
              label={module.label || "Date & Time"}
              value={responses[key] || ""}
              onChange={(e) => handleDateTimeChange(key, e.target.value)}
              fullWidth
              required={module.required}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              type="datetime-local"
              InputProps={{
                readOnly: !isFieldEditable,
              }}
              disabled={!isFieldEditable}
            />
          </Box>
        );

      case "table":
        const tableRows = responses[key] || [];
        return (
          <Box key={key} marginY={2}>
            <Typography variant="h6">{module.label}</Typography>

            {isMobile ? (
              // Stacked card layout for small screens
              <Box>
                {tableRows.map((row, rowIndex) => (
                  <Card key={rowIndex} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ pb: "12px !important" }}>
                      {module.columns.map((col, colIndex) => (
                        <Box key={colIndex} sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {col}
                          </Typography>
                          <TextField
                            value={row[col]}
                            onChange={(e) =>
                              handleTableCellChange(
                                key,
                                rowIndex,
                                col,
                                e.target.value,
                              )
                            }
                            fullWidth
                            size="small"
                            InputProps={{ readOnly: !isFieldEditable }}
                            disabled={!isFieldEditable}
                          />
                        </Box>
                      ))}
                      {isFieldEditable && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <IconButton
                            onClick={() => handleRemoveTableRow(key, rowIndex)}
                            color="error"
                            size="small"
                          >
                            <RemoveCircleIcon />
                          </IconButton>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              // Standard table layout for larger screens
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {module.columns.map((col, colIndex) => (
                        <TableCell key={colIndex}>{col}</TableCell>
                      ))}
                      {isFieldEditable && (
                        <TableCell align="right">Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {module.columns.map((col, colIndex) => (
                          <TableCell key={colIndex}>
                            <TextField
                              value={row[col]}
                              onChange={(e) =>
                                handleTableCellChange(
                                  key,
                                  rowIndex,
                                  col,
                                  e.target.value,
                                )
                              }
                              fullWidth
                              InputProps={{
                                readOnly: !isFieldEditable,
                              }}
                              disabled={!isFieldEditable}
                            />
                          </TableCell>
                        ))}
                        {isFieldEditable && (
                          <TableCell align="right">
                            <IconButton
                              onClick={() => handleRemoveTableRow(key, rowIndex)}
                              color="error"
                            >
                              <RemoveCircleIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {isFieldEditable && (
              <Button
                variant="outlined"
                startIcon={<AddCircleIcon />}
                onClick={() => handleAddTableRow(key, module.columns)}
                sx={{ marginTop: 1 }}
              >
                Add Row
              </Button>
            )}
          </Box>
        );

      case "email":
        return (
          <Box key={key} marginY={2}>
            <TextField
              label={module.label || "Email"}
              value={responses[key] || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              fullWidth
              required={module.required}
              margin="normal"
              type="email"
              InputProps={{
                readOnly: !isFieldEditable,
              }}
              disabled={!isFieldEditable}
            />
            {isFieldEditable && (
              <Button
                variant="contained"
                color="secondary"
                onClick={confirmEmail}
                disabled={emailConfirmed}
                sx={{
                  marginTop: 2,
                  backgroundColor: styles.buttonColor || "secondary.main",
                  "&:hover": {
                    backgroundColor: styles.buttonColor || "secondary.dark",
                  },
                }}
              >
                {emailConfirmed ? "Confirmed" : "Confirm"}
              </Button>
            )}
            {emailError && (
              <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
                {emailError}
              </Typography>
            )}
          </Box>
        );
      case "double_short_answer":
        return (
          <Box key={key} marginY={2}>
            <Typography variant="h6">{module.label}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={module.answer1Label || "Answer 1"}
                  value={responses[module.id]?.answers[0] || ""}
                  onChange={(e) => {
                    const newAnswers = [
                      ...(responses[module.id]?.answers || []),
                    ];
                    newAnswers[0] = e.target.value;
                    handleChange(`${module.id}.answers`, newAnswers);
                  }}
                  fullWidth
                  required={module.required}
                  margin="normal"
                  InputProps={{
                    readOnly: !isFieldEditable,
                  }}
                  disabled={!isFieldEditable}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={module.answer2Label || "Answer 2"}
                  value={responses[module.id]?.answers[1] || ""}
                  onChange={(e) => {
                    const newAnswers = [
                      ...(responses[module.id]?.answers || []),
                    ];
                    newAnswers[1] = e.target.value;
                    handleChange(`${module.id}.answers`, newAnswers);
                  }}
                  fullWidth
                  required={module.required}
                  margin="normal"
                  InputProps={{
                    readOnly: !isFieldEditable,
                  }}
                  disabled={!isFieldEditable}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case "radio_button_group":
        return (
          <FormControl
            component="fieldset"
            key={key}
            margin="normal"
            required={module.required}
            disabled={!isFieldEditable}
          >
            <FormLabel component="legend">{module.label}</FormLabel>
            <RadioGroup
              value={responses[key] || ""}
              onChange={(e) => handleChange(key, e.target.value)}
            >
              {module.options.map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
            {module.required && !responses[key] && (
              <FormHelperText error>Please select an option</FormHelperText>
            )}
          </FormControl>
        );

      case "user_details":
        return (
          <UserDetails
            key={key}
            data={responses[key] || {}}
            onChange={(field, value) => handleChange(`${key}.${field}`, value)}
            isEditable={isFieldEditable}
            styles={styles}
            setUserId={(id) => {
              if (!urlUserId) setUserId(id);
            }}
            setCompanyId={setCompanyId}
            multiCompanies={isMulti}
            userId={userId}
            preselectedCompanyId={urlCompanyId ? Number(urlCompanyId) : null}
          />
        );

      case "signature":
        return (
          <SignatureComponent
            key={module.id}
            label={module.label || "Signature"}
            signatureData={formData[key] || ""}
            onSignatureChange={(dataURL) =>
              handleSignatureChange(module.id || key, dataURL)
            }
            onRemove={() => handleSignatureChange(module.id || key, "")}
            isEditable={true}
          />
        );

      default:
        return null;
    }
  };

  if (!formStructure) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // **Filter out approval-required modules**
  const visibleModules = isPreview
    ? formStructure.modules
    : formStructure.modules.filter(
        (m) =>
          matchesStatus(m) &&
          !m.approvalRequired &&
          !(jobIdParam && m.type === "work_orders"),
      );

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <Container
          maxWidth="sm"
          sx={{
            // make room for a taller footer
            pb: "220px",
          }}
        >
          {visibleModules.map((module, index) => {
            // Assign fixed key for each module based on type or label/text
            let key = module.id;
            switch (module.type) {
              case "user_details":
                key = "user_details";
                break;
              case "email":
                key = "email";
                break;
              case "signature":
                key = "signature";
                break;
              default:
                key =
                  module.id || module.label || module.text || `module-${index}`;
            }

            return renderModule(module, key);
          })}

          {/* Render Document Upload Fields */}
          {requiredDocuments.length > 0 && (
            <Box marginY={2}>
              <Typography variant="h6" gutterBottom>
                Required Documents
              </Typography>
              {requiredDocuments.map((doc) => (
                <Box key={doc.id} marginY={1}>
                  <Typography variant="subtitle1">
                    {doc.name} {doc.required && "*"}
                  </Typography>
                  {isFieldEditable ? (
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      component="label"
                      sx={{ marginRight: 2 }}
                    >
                      {uploadedFiles[doc.id] ? "Change File" : "Upload File"}
                      <input
                        type="file"
                        hidden
                        onChange={(e) =>
                          handleFileChange(doc.id, e.target.files[0])
                        }
                      />
                    </Button>
                  ) : (
                    <Typography variant="body2">
                      {uploadedFiles[doc.id]?.name || "No file uploaded"}
                    </Typography>
                  )}
                  {!doc.required && (
                    <Typography variant="caption" color="textSecondary">
                      (Optional)
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {isHazardPopupOpen && (
            <HazardPopup
              originalHazards={hazards}
              onSave={handleLocalSave} // Save locally
              onApply={handleApplyChanges} // Apply to backend
              onClose={() => setHazardPopupOpen(false)}
              open={isHazardPopupOpen}
              currentUserId={userId}
            />
          )}

          {isZoneHazardPopupOpen && (
            <ZoneHazardPopup
              open={isZoneHazardPopupOpen}
              onClose={() => setZoneHazardPopupOpen(false)}
              siteId={siteId}
              zoneName={"Roof"}
            />
          )}

          <Dialog
            open={isModalOpen}
            onClose={() => setModalOpen(false)}
            aria-labelledby="incorrect-answers-dialog-title"
          >
            <DialogTitle id="incorrect-answers-dialog-title">
              Incorrect Answers
            </DialogTitle>
            <DialogContent>
              {incorrectModules.map((module, idx) => (
                <div key={idx}>
                  <Typography variant="h6">
                    {module.label || module.questionLabel}
                  </Typography>
                  <Typography variant="body1">
                    Selected Incorrect Answer(s):
                  </Typography>
                  <ul>
                    {incorrectAnswers[module.id]?.map((answer, idx) => (
                      <li key={idx}>{answer}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setModalOpen(false)}
                color="primary"
                variant="contained"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for Apply Success and Error */}
          <Snackbar
            open={applySuccess}
            autoHideDuration={6000}
            onClose={() => setApplySuccess(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setApplySuccess(false)}
              severity="success"
              sx={{ width: "100%" }}
            >
              Changes applied successfully!
            </Alert>
          </Snackbar>

          <Snackbar
            open={Boolean(applyError)}
            autoHideDuration={6000}
            onClose={() => setApplyError("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setApplyError("")}
              severity="error"
              sx={{ width: "100%" }}
            >
              {applyError}
            </Alert>
          </Snackbar>

          <Snackbar
            open={success}
            message="Form submitted successfully!"
            autoHideDuration={6000}
            onClose={() => setSuccess(false)}
            action={
              uploadedUrl ? (
                <Button
                  color="secondary"
                  size="small"
                  onClick={() => window.open(uploadedUrl, "_blank")}
                >
                  View Response
                </Button>
              ) : null
            }
          />
        </Container>
      </LocalizationProvider>

      {!isPreview && (
        <>
          <Box
            component="footer"
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              backgroundColor: "background.paper",
              boxShadow: 3,
              px: 2,
              py: 1,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            {!isAtBottom && (
              <Typography
                variant="caption"
                align="center"
                sx={{ display: "block", mb: 1 }}
              >
                ↓ Scroll down to finish the form ↓
              </Typography>
            )}

            {/* 🔔 Predicted Audit Status Bar */}
            {auditBanner && (
              <Box
                sx={{
                  mb: 1,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: auditBanner.color,
                  color: "#fff",
                  textAlign: "center",
                  fontWeight: 600,
                  maxWidth: 600, // wrap at 600px
                  mx: "auto",
                  whiteSpace: "normal",
                  fontSize: "11px",
                  wordBreak: "break-word",
                }}
              >
                {/* Score percentage */}
                {auditBanner.percentLabel && (
                  <Typography
                    component="span"
                    sx={{
                      display:     "block",
                      fontSize:    "1.4rem",
                      fontWeight:  700,
                      lineHeight:  1.1,
                      letterSpacing: 0,
                      mb: 0.25,
                    }}
                  >
                    {auditBanner.percentLabel}
                  </Typography>
                )}
                {/* Status label */}
                <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                  {auditBanner.mandatoryFailBanner
                    ? "FAIL — Critical Risk Identified"
                    : `This Audit will submit as ${auditBanner.text}${
                        auditBanner.showCounts ? ` — ${countsText}` : ""
                      }`}
                </Typography>
              </Box>
            )}

            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              sx={{ width: "100%", maxWidth: 300, mx: "auto" }} // <-- pick whatever total width you want
            >
              <Button
                fullWidth // <-- makes button stretch to 100% of parent
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : "Submit Form"}
              </Button>
              {formType !== "Induction" && formType !== "Company Induction" && (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleInProgress}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Save as In-Progress"
                    )}
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        </>
      )}
    </>
  );
}

export default FormRenderer;